import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { memberAdminCodes, useAuth } from '../../lib/auth';
import type { DataClassification, Member, Page, SeyxService } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, StatusBadge } from '../../ui/components';

/**
 * There is no GET endpoint that lists a member's registered services either
 * (only GET /members exists, and it does not include `services`) — same gap
 * as certificates, same workaround: remember what this browser tab
 * registered. See Members.tsx's own comment for the full reasoning.
 */
interface SessionService extends SeyxService {
  mdaCode: string;
}
const SERVICES_KEY = 'seyx.servicesSession';

function readServices(): SessionService[] {
  const raw = sessionStorage.getItem(SERVICES_KEY);
  return raw ? (JSON.parse(raw) as SessionService[]) : [];
}

const CLASSIFICATIONS: DataClassification[] = ['PUBLIC', 'OFFICIAL', 'PERSONAL', 'SENSITIVE'];

export function Services() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isDict = roles.includes('SEYX_ADMIN');
  const myMdas = memberAdminCodes(roles);
  const canRegister = isDict || myMdas.length > 0;

  const [showRegister, setShowRegister] = useState(false);
  const [sessionServices, setSessionServices] = useState<SessionService[]>(() => readServices());

  // Only SEYX_ADMIN can list members (registry.ts gates GET /members to
  // that role) — used purely to populate the "which MDA" picker below.
  const membersQ = useApiQuery<Page<Member>>(isDict ? '/api/v1/seyx/members?limit=100' : null);

  const persist = (items: SessionService[]) => {
    setSessionServices(items);
    sessionStorage.setItem(SERVICES_KEY, JSON.stringify(items));
  };

  if (!canRegister) {
    return (
      <div>
        <PageHeader title="Services" subtitle="Service registration — OpenAPI spec &amp; data classification" />
        <EmptyState title="No access" hint="This screen needs SEYX_ADMIN, or a MEMBER_ADMIN:<code> role for your own MDA." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Services"
        subtitle="Service registration — OpenAPI spec & data classification"
        actions={
          <button className="btn-primary" onClick={() => setShowRegister(true)}>
            + Register service
          </button>
        }
      />

      <div className="card overflow-x-auto">
        <div className="border-b border-slate-100 px-4 py-2.5 text-xs text-slate-400">
          There is no GET .../services listing endpoint yet — this app remembers what you register in this browser tab.
          Re-registering the same serviceCode bumps the version and deprecates the previous one (the real backend
          behaviour, not a display artefact).
        </div>
        {sessionServices.length === 0 ? (
          <EmptyState title="No services registered this session" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">MDA</th>
                <th className="table-head">Service code</th>
                <th className="table-head">Version</th>
                <th className="table-head">Classification</th>
                <th className="table-head">Backend URL</th>
                <th className="table-head">Registered</th>
              </tr>
            </thead>
            <tbody>
              {sessionServices.map((s) => (
                <tr key={s.id}>
                  <td className="table-cell font-mono text-xs">{s.mdaCode}</td>
                  <td className="table-cell font-mono text-xs">{s.serviceCode}</td>
                  <td className="table-cell">v{s.version}</td>
                  <td className="table-cell">
                    <StatusBadge status={s.dataClassification} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{s.backendUrl}</td>
                  <td className="table-cell text-slate-500">{formatDateTime(s.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showRegister ? (
        <RegisterModal
          isDict={isDict}
          myMdas={myMdas}
          knownMembers={membersQ.data?.items ?? []}
          onClose={() => setShowRegister(false)}
          onDone={(svc, mdaCode) => {
            setShowRegister(false);
            persist([{ ...svc, mdaCode }, ...sessionServices]);
          }}
        />
      ) : null}
    </div>
  );
}

function RegisterModal({
  isDict,
  myMdas,
  knownMembers,
  onClose,
  onDone,
}: {
  isDict: boolean;
  myMdas: string[];
  knownMembers: Member[];
  onClose: () => void;
  onDone: (svc: SeyxService, mdaCode: string) => void;
}) {
  const [mdaCode, setMdaCode] = useState(isDict ? '' : (myMdas[0] ?? ''));
  const [serviceCode, setServiceCode] = useState('');
  const [dataClassification, setDataClassification] = useState<DataClassification>('OFFICIAL');
  const [backendUrl, setBackendUrl] = useState('');
  const [openApiSpec, setOpenApiSpec] = useState(() =>
    JSON.stringify({ openapi: '3.1.0', info: { title: 'New service', version: '1.0.0' }, paths: {} }, null, 2),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const specError = useMemo(() => {
    try {
      const parsed = JSON.parse(openApiSpec);
      if (typeof parsed?.openapi !== 'string') return 'Missing "openapi" version string';
      if (typeof parsed?.info?.title !== 'string') return 'Missing "info.title"';
      return null;
    } catch {
      return 'Not valid JSON';
    }
  }, [openApiSpec]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const svc = await api.post<SeyxService>(`/api/v1/seyx/members/${mdaCode}/services`, {
        serviceCode,
        openApiSpec: JSON.parse(openApiSpec),
        dataClassification,
        backendUrl,
      });
      onDone(svc, mdaCode);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Service registration failed');
    } finally {
      setBusy(false);
    }
  };

  const validCode = /^[a-z0-9-]{2,80}$/.test(serviceCode);

  return (
    <Modal title="Register service" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-3 gap-3">
          <Field label="MDA">
            {isDict ? (
              <select className="field-input" value={mdaCode} onChange={(e) => setMdaCode(e.target.value)}>
                <option value="">Select member…</option>
                {knownMembers.map((m) => (
                  <option key={m.id} value={m.mdaCode}>
                    {m.mdaCode} — {m.name}
                  </option>
                ))}
              </select>
            ) : myMdas.length > 1 ? (
              <select className="field-input" value={mdaCode} onChange={(e) => setMdaCode(e.target.value)}>
                {myMdas.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            ) : (
              <input className="field-input font-mono" value={mdaCode} disabled />
            )}
          </Field>
          <Field label="Service code (a-z, 0-9, -)">
            <input
              className="field-input font-mono"
              value={serviceCode}
              onChange={(e) => setServiceCode(e.target.value.toLowerCase())}
              placeholder="tax-clearance-status"
            />
          </Field>
          <Field label="Data classification">
            <select className="field-input" value={dataClassification} onChange={(e) => setDataClassification(e.target.value as DataClassification)}>
              {CLASSIFICATIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {(dataClassification === 'PERSONAL' || dataClassification === 'SENSITIVE') && (
          <p className="text-xs text-amber-600">
            {dataClassification} data: any access right against this service will need a legal basis reference (C7)
            before DICT can approve it.
          </p>
        )}
        <Field label="Backend URL (where the exchange gateway routes calls to)">
          <input className="field-input" value={backendUrl} onChange={(e) => setBackendUrl(e.target.value)} placeholder="https://api.src.gov.sc/exchange/tax-clearance-status" />
        </Field>
        <Field label="OpenAPI spec (JSON)">
          <textarea className="field-input font-mono text-xs" rows={7} value={openApiSpec} onChange={(e) => setOpenApiSpec(e.target.value)} />
        </Field>
        {specError ? <p className="text-xs text-rose-600">{specError}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !mdaCode || !validCode || !backendUrl || Boolean(specError)}>
            {busy ? 'Registering…' : 'Register service'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
